import { useState } from "react";

interface FilterBarProps {
  sort: "latest" | "hot";
  onSortChange: (value: "latest" | "hot") => void;
  selectedRegions: string[];
  onToggleRegion: (region: string) => void;
  onClear: () => void;
  regionOptions: string[];
  resultCount: number;
}

const sortOptions: { value: "latest" | "hot"; label: string }[] = [
  { value: "latest", label: "最新发布" },
  { value: "hot", label: "最多阅读" },
];

export default function FilterBar({
  sort,
  onSortChange,
  selectedRegions,
  onToggleRegion,
  onClear,
  regionOptions,
  resultCount,
}: FilterBarProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const activeCount = selectedRegions.length + (sort === "hot" ? 1 : 0);

  const filterBody = (
    <>
      <div>
        <p className="text-[11px] font-label tracking-widest text-foreground-400 mb-2">时间排序</p>
        <div className="flex items-center gap-2 p-1 rounded-md bg-secondary-100 w-max max-w-full">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onSortChange(option.value)}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                sort === option.value
                  ? "bg-primary-600 text-background-50"
                  : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[11px] font-label tracking-widest text-foreground-400 mb-2">
          地域筛选（可多选）
        </p>
        <div className="flex flex-wrap gap-2">
          {regionOptions.map((region) => {
            const active = selectedRegions.includes(region);
            return (
              <button
                key={region}
                type="button"
                onClick={() => onToggleRegion(region)}
                aria-pressed={active}
                className={`px-3 py-1.5 rounded-md text-sm border transition-colors cursor-pointer whitespace-nowrap ${
                  active
                    ? "bg-accent-100 border-accent-300 text-accent-900"
                    : "bg-background-50 border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {region}
                {active && (
                  <span className="ml-1 inline-flex w-3 h-3 items-center justify-center align-middle">
                    <i className="ri-check-line text-[10px]" aria-hidden="true" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="hidden lg:flex items-end justify-between gap-6 rounded-lg border border-background-200 bg-background-100 px-5 py-4">
        <div className="flex items-end gap-8 flex-wrap flex-1">{filterBody}</div>
        <button
          type="button"
          onClick={onClear}
          className="shrink-0 inline-flex items-center gap-1 h-9 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-refresh-line" aria-hidden="true" />
          </span>
          清空筛选
        </button>
      </div>

      <div className="lg:hidden flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-800 cursor-pointer"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-filter-3-line" aria-hidden="true" />
          </span>
          筛选与排序
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-primary-600 text-background-50 text-[11px] font-label">
              {activeCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={onClear}
          className="h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
        >
          重置
        </button>
      </div>

      <div
        className={`lg:hidden fixed inset-0 z-50 transition-opacity ${
          sheetOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!sheetOpen}
      >
        <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setSheetOpen(false)} />
        <div
          role="dialog"
          aria-modal="true"
          aria-label="筛选与排序"
          className={`absolute inset-x-0 bottom-0 bg-background-50 rounded-t-lg transition-transform duration-300 ${
            sheetOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200">
            <p className="font-heading text-base font-bold text-foreground-950">筛选与排序</p>
            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              aria-label="关闭筛选"
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-close-line text-xl" aria-hidden="true" />
            </button>
          </div>
          <div className="px-5 py-5 space-y-5 max-h-[60vh] overflow-y-auto">{filterBody}</div>
          <div className="px-5 pb-6 pt-3 flex items-center gap-3 border-t border-background-200">
            <button
              type="button"
              onClick={() => {
                onClear();
                setSheetOpen(false);
              }}
              className="flex-1 h-11 rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer"
            >
              清空筛选
            </button>
            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              className="flex-1 h-11 rounded-md bg-primary-600 text-background-50 text-sm font-medium cursor-pointer"
            >
              查看 {resultCount} 条结果
            </button>
          </div>
        </div>
      </div>
    </>
  );
}